/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import _ from 'lodash';
import { createForm } from '../../helpers/form';
import t from '@amzn/base-ui/dist/models/texts/ui-texts'

const enablePrefillWorkspace = process.env.REACT_APP_ENABLE_PREFILL_WORKSPACE === 'true';
const enablePrefillWorkspaceName = process.env.REACT_APP_ENABLE_PREFILL_WORKSPACE_NAME === 'true';

const getFields = ({
  projectIdOptions,
  cidr,
  regionOptions,
  rootVolumeSizeRange = { min: 32, max: 2048, default: 32 },
  userName = '',
}) => {
  const fields = {
    name: {
      label: 'Workspace Name',
      placeholder: 'Combination of letters, numbers and -, starts with a letter.',
      rules: 'required|string|between:3,128|WorkspaceNameRule1|WorkspaceNameRule2|WorkspaceNameRule3',
      value: enablePrefillWorkspaceName ? t('workspaces', 'prefill-name', { template: {name: userName.split(" ").join("-")} }) : undefined
    },
    description: {
      label: 'Description',
      placeholder: 'The description of this workspace',
      rules: 'required|string|between:1,2048',
      value: enablePrefillWorkspace ? t('workspaces', 'prefill-description', { template: {name: userName} }) : undefined
    },
    projectId: {
      label: 'Project ID',
      placeholder: 'The project ID to associate with this workspace',
      value: projectIdOptions[0].value,
      rules: ['required', 'string', 'min:1', 'max:100'],
      extra: {
        options: projectIdOptions,
        // explain: 'The project ID you wish to charge usage to.',
      },
    },
    rootVolume: {
      label: 'C Drive/Root Volume Size (GB)',
      value: `${Math.max(rootVolumeSizeRange.min, rootVolumeSizeRange.default ?? 32)}`,
      rules: ['required', 'integer', `min:${rootVolumeSizeRange.min}`, `max:${rootVolumeSizeRange.max}`],
      extra: {
        explain: 'Adjust the amount of storage space available on your workspace.',
      },
    },
    envTypeConfigId: {
      label: 'Instance Type',
      placeholder: 'The configuration for the workspace',
      extra: {
        explain: 'Select your hardware configuration. Looking for something not listed? Try selecting a different region, or contact us at race@rmit.edu.au.',
      },
      rules: 'required',
    },
    enableHyperthreading: {
      label: 'Hyperthreading',
      extra: {
        explain: 'If disabled, this will half your number of vCPUs, where 1 vCPU will equal 1 physical core.',
      },
      value: true,
      rules: ['boolean', 'required'],
    },
    crossCommunication: {
      label: t('workspaces', 'cross-communication-label'),
      extra: {
        explain: 'If enabled, this workspace can communicate with other workspaces within your project.'
      },
      value: true,
      rules: ['boolean', 'required'],
    },
  };

  if (!_.isUndefined(cidr)) {
    fields.cidr = {
      label: 'Restricted CIDR',
      extra: {
        explain: `This is your IP address. Your workspace will only be reachable from this CIDR (IP address range) and on campus. You can change your IP address whitelist after creating the workspace.`,
      },
      placeholder: 'The CIDR range to restrict workspace access to',
      rules: 'required|cidr',
      value: cidr,
    };
  }

  if (!_.isEmpty(regionOptions)) {
    fields.targetRegion = {
      label: 'Target Region',
      placeholder: 'Select the AWS region for this workspace',
      extra: {
        explain: 'The region where your workspace will be deployed. The available hardware configurations may vary by region.',
        options: regionOptions,
      },
      rules: 'required|string',
      value: regionOptions[0].value,
    };
  }

  return fields;
};

function getCreateInternalEnvForm(...args) {
  return createForm(getFields(...args));
}

// eslint-disable-next-line import/prefer-default-export
export { getCreateInternalEnvForm };
